import toast from 'react-hot-toast';

/**
 * Custom toast utilities for consistent notifications across the marketplace
 * Provides standardized success, error, warning, and info notifications
 */

// Toast types with consistent styling and behavior
export const toastTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  LOADING: 'loading',
};

// Default toast options
const defaultOptions = {
  position: 'top-right',
  duration: 4000,
};

/**
 * Success toast notification
 * @param {string} message - Success message to display
 * @param {object} options - Additional toast options
 */
export const showSuccess = (message, options = {}) => {
  return toast.success(message, {
    ...defaultOptions,
    duration: 3000,
    ...options,
  });
};

/**
 * Error toast notification
 * @param {string} message - Error message to display
 * @param {object} options - Additional toast options
 */
export const showError = (message, options = {}) => {
  return toast.error(message, {
    ...defaultOptions,
    duration: 5000,
    ...options,
  });
};

/**
 * Warning toast notification
 * @param {string} message - Warning message to display
 * @param {object} options - Additional toast options
 */
export const showWarning = (message, options = {}) => {
  return toast(message, {
    ...defaultOptions,
    duration: 4000,
    icon: '⚠️',
    style: {
      ...defaultOptions.style,
      background: '#F59E0B',
      color: '#fff',
    },
    ...options,
  });
};

/**
 * Info toast notification
 * @param {string} message - Info message to display
 * @param {object} options - Additional toast options
 */
export const showInfo = (message, options = {}) => {
  return toast(message, {
    ...defaultOptions,
    duration: 4000,
    icon: 'ℹ️',
    style: {
      ...defaultOptions.style,
      background: '#3B82F6',
      color: '#fff',
    },
    ...options,
  });
};

/**
 * Loading toast notification
 * @param {string} message - Loading message to display
 * @param {object} options - Additional toast options
 */
export const showLoading = (message, options = {}) => {
  return toast.loading(message, {
    ...defaultOptions,
    duration: Infinity, // Loading toasts don't auto-dismiss
    ...options,
  });
};

/**
 * Dismiss a specific toast
 * @param {string} toastId - ID of the toast to dismiss
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

/**
 * Promise-based toast for async operations
 * @param {Promise} promise - Promise to handle
 * @param {object} options - Toast options
 * @param {string} options.loading - Loading message
 * @param {string} options.success - Success message
 * @param {string} options.error - Error message
 */
export const toastPromise = (promise, options) => {
  const { loading, success, error } = options;
  
  return toast.promise(
    promise,
    {
      loading: loading || 'Loading...',
      success: success || 'Success!',
      error: error || 'Something went wrong',
    },
    {
      ...defaultOptions,
      success: {
        ...defaultOptions,
        duration: 3000,
      },
      error: {
        ...defaultOptions,
        duration: 5000,
      },
    }
  );
};

/**
 * Custom toast for marketplace-specific actions
 */

// Product-related toasts
export const productToasts = {
  added: (productName) => showSuccess(`${productName} added to cart!`),
  removed: (productName) => showSuccess(`${productName} removed from cart`),
  updated: (productName) => showSuccess(`${productName} updated successfully`),
  deleted: (productName) => showSuccess(`${productName} deleted successfully`),
  created: (productName) => showSuccess(`${productName} created successfully!`),
  reordered: () => showSuccess('Product order updated successfully'),
  error: (action) => showError(`Failed to ${action} product`),
};

// Shop-related toasts
export const shopToasts = {
  saved: () => showSuccess('Shop settings saved successfully'),
  updated: () => showSuccess('Shop updated successfully'),
  error: (action) => showError(`Failed to ${action} shop`),
};

// Order-related toasts
export const orderToasts = {
  placed: () => showSuccess('Order placed successfully!'),
  updated: () => showSuccess('Order updated successfully'),
  cancelled: () => showSuccess('Order cancelled successfully'),
  success: (message) => showSuccess(message || 'Operation completed successfully'),
  error: (action) => showError(`Failed to ${action} order`),
};

// User-related toasts
export const userToasts = {
  loggedIn: () => showSuccess('Welcome back!'),
  loggedOut: () => showInfo('You have been logged out'),
  profileUpdated: () => showSuccess('Profile updated successfully'),
  error: (action) => showError(`Failed to ${action}`),
};

// Form-related toasts
export const formToasts = {
  saved: () => showSuccess('Form saved successfully'),
  submitted: () => showSuccess('Form submitted successfully'),
  validationError: () => showError('Please check your input and try again'),
  error: (action) => showError(`Failed to ${action}`),
};

// File upload toasts
export const uploadToasts = {
  started: (fileName) => showInfo(`Uploading ${fileName}...`),
  success: (fileName) => showSuccess(`${fileName} uploaded successfully`),
  error: (fileName) => showError(`Failed to upload ${fileName}`),
  progress: (fileName, progress) => showInfo(`Uploading ${fileName}: ${progress}%`),
};

// Network-related toasts
export const networkToasts = {
  offline: () => showWarning('You are currently offline'),
  online: () => showSuccess('Connection restored'),
  slow: () => showWarning('Slow connection detected'),
  error: () => showError('Network error occurred'),
};

// Default export for convenience
export default {
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  loading: showLoading,
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
  promise: toastPromise,
  product: productToasts,
  shop: shopToasts,
  order: orderToasts,
  user: userToasts,
  form: formToasts,
  upload: uploadToasts,
  network: networkToasts,
};
